import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Check, CircleAlert, CircleCheck, CircleHelp, ExternalLink, Globe, Search, ShieldCheck, SlidersHorizontal, ThumbsDown, ThumbsUp,
} from 'lucide-react';
import { AppShell, Avatar, Button, EmptyState, Loader, PageHeader, Segmented } from '../components/ui';
import { useAuth } from '../contexts/AuthContext';
import { apiErrorMessage, articleReviewService } from '../services/api';
import { findingMeta, severityLabel } from '../components/review/findings';
import { ago } from '../utils/ai';
import './AdminArticleReviews.css';

const TABS = [
  { value: 'pending', label: 'Aguardando' },
  { value: 'approved', label: 'Liberados' },
  { value: 'rejected', label: 'Devolvidos' },
];

/** Revisão de artigos (/admin/revisao): artigos públicos com "Não é problema" esperando um admin. */
function AdminArticleReviews() {
  const { isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState('pending');
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [selectedId, setSelectedId] = useState(params.get('id') ? Number(params.get('id')) : null);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const d = await articleReviewService.queue(tab, q || undefined);
      setData(d);
      setError(null);
      setSelectedId(cur => (cur && d.items.some(i => i.id === cur) ? cur : d.items[0]?.id ?? cur));
    } catch (err) {
      setError(apiErrorMessage(err, 'Não deu para carregar a fila.'));
    }
  }, [tab, q]);

  useEffect(() => {
    const t = setTimeout(load, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    let alive = true;
    articleReviewService.detail(selectedId)
      .then(d => { if (alive) setDetail(d); })
      .catch(() => { if (alive) setDetail(null); });
    return () => { alive = false; };
  }, [selectedId]);

  const select = (id) => {
    setSelectedId(id);
    setParams(id ? { id: String(id) } : {}, { replace: true });
  };

  const onDecided = (d) => {
    setDetail(d);
    window.dispatchEvent(new CustomEvent('article-reviews:changed'));
    load();
  };

  const counts = data?.counts || {};

  return (
    <AppShell section="Revisão de artigos">
      <PageHeader
        eyebrow="Administração"
        title="Revisão de artigos"
        subtitle={'Artigos públicos em que o autor marcou "Não é problema" num ponto da revisão por IA.'}
        actions={isSuperAdmin && (
          <Button variant="outline" icon={SlidersHorizontal} onClick={() => navigate('/admin/ia')}>Regras da revisão</Button>
        )}
      />

      <div className="arv-toolbar">
        <Segmented
          ariaLabel="Situação"
          value={tab}
          onChange={(v) => { setTab(v); select(null); }}
          options={TABS.map(t => ({ ...t, count: counts[t.value] ?? 0 }))}
        />
        <label className="arv-search">
          <Search size={15} aria-hidden="true" />
          <input className="ss-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por título ou autor" />
        </label>
      </div>

      {error && <p className="arv-error" role="alert"><CircleAlert size={16} aria-hidden="true" />{error}</p>}

      {!data ? <Loader /> : data.items.length === 0 ? (
        <EmptyState
          title={tab === 'pending' ? 'Nada esperando revisão' : tab === 'approved' ? 'Nenhum artigo liberado ainda' : 'Nenhum artigo devolvido'}
          description={tab === 'pending' ? 'Quando um autor justificar um ponto da revisão por IA, o artigo aparece aqui.' : undefined}
        />
      ) : (
        <div className="arv-grid">
          <ul className="arv-list" aria-label="Artigos">
            {data.items.map(it => (
              <li key={it.id}>
                <button type="button" className={`arv-item ${it.id === selectedId ? 'is-active' : ''}`} onClick={() => select(it.id)}
                        aria-current={it.id === selectedId}>
                  <Avatar user={{ avatarUrl: it.author.avatarUrl || null }} initials={it.author.initials} size={34} />
                  <span className="arv-item__text">
                    <strong>{it.title}</strong>
                    <span>{it.author.name} · {ago(it.updatedAt)}</span>
                    {it.types.length > 0 && (
                      <span className="arv-item__types">
                        {it.types.map(t => <span key={t} className="arv-pill">{t}</span>)}
                      </span>
                    )}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          {detail ? <Detail key={detail.review.id} detail={detail} onDecided={onDecided} /> : <div className="arv-detail arv-detail--empty"><Loader /></div>}
        </div>
      )}
    </AppShell>
  );
}

function Detail({ detail, onDecided }) {
  const { article, review, author } = detail;
  const pending = review.status === 'PENDING';
  const [comment, setComment] = useState('');
  const [decisions, setDecisions] = useState(() => Object.fromEntries(
    review.findings.filter(f => f.adminDecision).map(f => [f.id, f.adminDecision])));
  const [deciding, setDeciding] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const justified = review.findings.filter(f => f.state === 'DISMISSED');
  const undecided = justified.filter(f => !decisions[f.id]).length;
  const refused = justified.some(f => decisions[f.id] === 'REJECT');

  const act = async (kind) => {
    if (kind === 'reject' && !comment.trim()) {
      setError('Escreva um comentário para o autor antes de pedir correção.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const d = kind === 'approve'
        ? await articleReviewService.approve(review.id, comment.trim() || undefined, decisions)
        : await articleReviewService.reject(review.id, comment.trim(), decisions);
      onDecided(d);
    } catch (err) {
      setError(apiErrorMessage(err, 'Não deu para concluir.'));
    } finally {
      setBusy(false);
    }
  };

  const status = useMemo(() => {
    if (review.status === 'APPROVED') return `Liberado por ${detail.reviewer?.name || 'um admin'} ${ago(detail.reviewedAt)}`;
    if (review.status === 'REJECTED') return `Devolvido por ${detail.reviewer?.name || 'um admin'} ${ago(detail.reviewedAt)}`;
    return null;
  }, [review.status, detail]);

  return (
    <section className="arv-detail" aria-label="Detalhe da revisão">
      <header className="arv-detail__head">
        <div className="arv-detail__meta">
          <span className="arv-detail__tags">
            {article.tags.map(t => <span key={t}>#{t}</span>)}
            <span className="arv-public"><Globe size={11} aria-hidden="true" />Público</span>
          </span>
          <h2>{article.title}</h2>
          <span className="arv-detail__by">{author.name} · enviado {ago(review.updatedAt)} · revisão por IA {ago(review.createdAt)}</span>
        </div>
        <Button variant="outline" size="sm" icon={ExternalLink}
                onClick={() => window.open(`/artigos/${article.slug}`, "_blank", "noopener")}>Abrir artigo</Button>
      </header>

      <div className="arv-detail__body">
        {article.changedSinceReview && pending && (
          <p className="arv-warn"><CircleAlert size={15} aria-hidden="true" />O autor mudou o texto depois desta revisão. Peça uma nova revisão.</p>
        )}
        <span className="arv-eyebrow">Resultado da IA · {review.findings.length} {review.findings.length === 1 ? 'ponto' : 'pontos'}</span>
        <ul className="arv-findings">
          {review.findings.map(f => {
            const meta = findingMeta(f);
            const fixed = f.state === 'FIXED';
            const decision = decisions[f.id];
            return (
              <li key={f.id} className={`arv-finding ${fixed ? 'is-fixed' : 'is-justified'}`}>
                <span className="arv-finding__icon"><meta.icon size={15} aria-hidden="true" /></span>
                <div className="arv-finding__main">
                  <span className="arv-finding__type">{meta.label} · {severityLabel(f.severity)}</span>
                  <q>{f.excerpt}</q>
                  {f.justification && <p className="arv-finding__why"><span>Autor:</span> {f.justification}</p>}
                  {deciding === f.id && pending && (
                    <span className="arv-finding__decide">
                      <Button size="sm" variant="outline" icon={ThumbsUp}
                              onClick={() => { setDecisions(d => ({ ...d, [f.id]: 'ACCEPT' })); setDeciding(null); }}>Não é problema</Button>
                      <Button size="sm" variant="outline" icon={ThumbsDown}
                              onClick={() => { setDecisions(d => ({ ...d, [f.id]: 'REJECT' })); setDeciding(null); }}>Precisa trocar</Button>
                    </span>
                  )}
                </div>
                <span className="arv-finding__state">
                  {fixed ? <><CircleCheck size={14} aria-hidden="true" />Corrigido pelo autor</> : decision === 'ACCEPT' ? (
                    <button type="button" className="arv-decided is-ok" onClick={() => pending && setDeciding(f.id)}><Check size={14} aria-hidden="true" />Aceito</button>
                  ) : decision === 'REJECT' ? (
                    <button type="button" className="arv-decided is-no" onClick={() => pending && setDeciding(f.id)}><ThumbsDown size={14} aria-hidden="true" />Precisa trocar</button>
                  ) : pending ? (
                    <button type="button" className="arv-decide" onClick={() => setDeciding(deciding === f.id ? null : f.id)}>
                      <CircleHelp size={14} aria-hidden="true" />Decidir
                    </button>
                  ) : null}
                </span>
              </li>
            );
          })}
        </ul>

        {pending ? (
          <label className="arv-comment">
            <span><strong>Comentário para o autor</strong> · obrigatório para pedir correção</span>
            <textarea className="ss-input" rows={3} value={comment} onChange={(e) => setComment(e.target.value)}
                      placeholder={'Ex.: troque o nome do ambiente pelo genérico "homologação".'} maxLength={1000} />
          </label>
        ) : (
          <p className={`arv-outcome arv-outcome--${review.status === 'APPROVED' ? 'ok' : 'no'}`}>
            {review.status === 'APPROVED' ? <ShieldCheck size={15} aria-hidden="true" /> : <CircleAlert size={15} aria-hidden="true" />}
            <span>{status}{review.reviewerComment ? `: "${review.reviewerComment}"` : '.'}</span>
          </p>
        )}
        {error && <p className="arv-error" role="alert"><CircleAlert size={15} aria-hidden="true" />{error}</p>}
      </div>

      {pending && (
        <footer className="arv-detail__foot">
          <span>{undecided > 0 ? `${undecided} ${undecided === 1 ? 'ponto' : 'pontos'} sem decisão · ` : ''}Liberar publica na hora e registra quem liberou.</span>
          <Button variant="outline" className="arv-reject" onClick={() => act('reject')} disabled={busy}>Pedir correção</Button>
          <Button variant="accent" icon={Check} onClick={() => act('approve')}
                  disabled={busy || refused || article.changedSinceReview}
                  title={refused ? 'Há ponto marcado como "Precisa trocar"' : undefined}>
            Liberar e publicar
          </Button>
        </footer>
      )}
    </section>
  );
}

export default AdminArticleReviews;
